import { type YDatabase } from '@databases/collab/y-database';
import { type DatabaseSdk } from '@databases/sdk';

import { type CollabManagerLike } from './collab-manager-like.ts';

/**
 * A live connection to a Confluence Database, wrapping a {@link DatabaseSdk}
 * and owning the underlying collab session.
 *
 * Obtain an instance via {@link loadDatabase}. Call {@link dispose} when
 * finished to tear down the WebSocket and release resources.
 *
 * Implements `Symbol.dispose` for use with TC39 Explicit Resource Management
 * (`using db = await loadDatabase(opts)`).
 */
export class ConnectedDatabase {
  /**
   * The SDK for reading and mutating this database's fields, entries,
   * values, views, and metadata. Mutations on a connected SDK propagate
   * to all collaborators via the Yjs sync protocol.
   */
  readonly sdk: DatabaseSdk;

  /**
   * The underlying Yjs CRDT document backing this database.
   * Exposed for interop with infrastructure that operates on
   * the raw collab document (e.g. building Redux state shapes).
   */
  readonly yDatabase: YDatabase;

  /**
   * Whether this connection was opened in read-only mode. When `true`,
   * the underlying WebSocket will not send document updates.
   */
  readonly isReadOnly: boolean;

  #manager: CollabManagerLike | undefined;
  #disposed = false;

  constructor(
    sdk: DatabaseSdk,
    manager: CollabManagerLike,
    yDatabase: YDatabase,
    isReadOnly: boolean
  ) {
    this.sdk = sdk;
    this.yDatabase = yDatabase;
    this.isReadOnly = isReadOnly;
    this.#manager = manager;
  }

  /**
   * The underlying collab manager owning the WebSocket session.
   * Returns `undefined` after {@link dispose} has been called.
   */
  get manager(): CollabManagerLike | undefined {
    return this.#manager;
  }

  /**
   * Whether {@link dispose} has been called. Once disposed, the SDK
   * remains usable for local reads (e.g. `toJSON`) but mutations will
   * no longer propagate to the server.
   */
  get isDisposed(): boolean {
    return this.#disposed;
  }

  /**
   * Tears down the WebSocket connection and releases all collab resources.
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  dispose(): void {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;
    this.#manager?.killSession();
    this.#manager = undefined;
  }

  [Symbol.dispose](): void {
    this.dispose();
  }
}
